import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Wallet, Clock, CheckCircle2, AlertTriangle, FileDown } from "lucide-react";
import { PageHeader } from "@/components/app/PageHeader";

export default function ParentFeesPage() {
  const { t, lang } = useI18n();
  const [fees, setFees] = useState([]);
  const [phone, setPhone] = useState("");
  const [tpConfig, setTpConfig] = useState({ configured: false });
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/fees").then((r) => setFees(r.data.fees));

  useEffect(() => {
    load();
    api.get("/payments/toyyibpay/config").then((r) => setTpConfig(r.data)).catch(() => {});
  }, []);

  const downloadReceipt = async (fee) => {
    setBusy(fee.id + "receipt");
    try {
      const res = await api.get(`/fees/${fee.id}/receipt.pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `receipt-${fee.id.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(lang === "ms" ? "Gagal memuat turun resit" : "Failed to download receipt");
    } finally {
      setBusy(null);
    }
  };

  const pay = async (fee, provider) => {
    if (provider === "toyyibpay" && !phone) {
      toast.error(lang === "ms" ? "Sila isi nombor telefon" : "Please enter your phone number");
      return;
    }
    setBusy(fee.id + provider);
    try {
      const { data } = await api.post(`/fees/${fee.id}/checkout`, {
        origin_url: window.location.origin,
        provider,
        phone: phone || null,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
      setBusy(null);
    }
  };

  const pending = fees.filter((f) => f.status !== "paid");
  const paid = fees.filter((f) => f.status === "paid");

  return (
    <div className="space-y-6" data-testid="parent-fees">
      <PageHeader
        eyebrow="Kelas Program Membaca PMM"
        title={t("fees")}
        subtitle={lang === "ms" ? "Bayar yuran yang ditugaskan untuk anak anda." : "Pay the fees assigned to your children."}
      />

      <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md" style={{ animationDelay: "0.08s" }}>
        <CardContent className="p-6 max-w-md space-y-2">
          <Label>{lang === "ms" ? "Nombor telefon (untuk FPX)" : "Phone number (for FPX)"}</Label>
          <Input data-testid="pfee-phone" className="h-11" placeholder="+60123456789" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </CardContent>
      </Card>

      <section>
        <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950"><Clock className="h-4 w-4 text-orange-500" /> {t("pending")} ({pending.length})</h2>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {pending.map((f, i) => (
            <Card key={f.id} className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md transition-all duration-300 hover:-translate-y-1 hover:shadow-xl" data-testid={`pfee-${f.id}`} style={{ animationDelay: `${0.12 + i * 0.06}s` }}>
              <CardContent className="p-6">
                <div className="mb-3 grid h-11 w-11 place-items-center rounded-2xl bg-gradient-to-br from-orange-600 to-orange-400 text-white shadow-lg shadow-orange-500/30">
                  <Wallet className="h-5 w-5" />
                </div>
                <div className="font-display text-lg font-extrabold text-blue-950">{f.name}</div>
                <div className="text-sm font-semibold text-muted-foreground">{f.student_name}</div>
                <div className="num mt-3 font-display text-3xl font-extrabold tracking-tight text-blue-950">
                  {f.currency.toUpperCase()} {Number(f.amount).toFixed(2)}
                </div>
                <div className={`num mt-2 flex items-center gap-1 text-xs font-bold ${f.is_overdue ? "text-destructive" : "text-muted-foreground"}`}>
                  {f.is_overdue && <AlertTriangle className="h-3.5 w-3.5" />}
                  {t("dueDate")}: {f.due_date}
                </div>
                <div className="mt-4 grid grid-cols-2 gap-2">
                  <Button size="sm" onClick={() => pay(f, "stripe")} disabled={busy === f.id + "stripe"} className="h-10 rounded-full bg-gradient-to-r from-orange-600 to-orange-500 font-bold text-white shadow-md shadow-orange-500/30 transition-transform duration-300 hover:scale-105" data-testid={`pfee-stripe-${f.id}`}>Card</Button>
                  <Button size="sm" onClick={() => pay(f, "toyyibpay")} disabled={busy === f.id + "toyyibpay" || !tpConfig.configured} className="h-10 rounded-full bg-gradient-to-r from-blue-900 to-blue-700 font-bold text-white shadow-md shadow-blue-800/30 transition-transform duration-300 hover:scale-105" data-testid={`pfee-toyyib-${f.id}`}>FPX</Button>
                </div>
              </CardContent>
            </Card>
          ))}
          {pending.length === 0 && (
            <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-md md:col-span-2 lg:col-span-3"><CardContent className="p-6 text-sm font-semibold text-muted-foreground">
              {lang === "ms" ? "Tiada yuran belum bayar. Terima kasih!" : "No pending fees. Thank you!"}
            </CardContent></Card>
          )}
        </div>
      </section>

      {paid.length > 0 && (
        <section>
          <h2 className="mb-3 flex items-center gap-2 font-display text-sm font-extrabold uppercase tracking-widest text-blue-950"><CheckCheck className="h-4 w-4 text-green-600" /> {t("paid")} ({paid.length})</h2>
          <Card className="pmm-pop rounded-[1.5rem] border-border/60 shadow-lg" style={{ animationDelay: "0.15s" }}><CardContent className="p-4">
            <ul className="divide-y divide-border/60">
              {paid.map((f) => (
                <li key={f.id} className="flex items-center justify-between rounded-xl px-2 py-3 transition-colors hover:bg-orange-50">
                  <div>
                    <div className="font-display font-extrabold text-blue-950">{f.name}</div>
                    <div className="num text-xs font-semibold text-muted-foreground">{f.student_name} · {f.due_date}</div>
                  </div>
                  <div className="text-right">
                    <div className="num font-display font-extrabold text-blue-950">{f.currency.toUpperCase()} {Number(f.amount).toFixed(2)}</div>
                    <div className="flex items-center justify-end gap-1 text-xs font-bold text-green-600">
                      <CheckCircle2 className="h-3 w-3" /> {f.paid_via || "paid"}
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => downloadReceipt(f)}
                      disabled={busy === f.id + "receipt"}
                      className="mt-2 h-8 rounded-full gap-1 text-xs"
                      data-testid={`receipt-${f.id}`}
                    >
                      <FileDown className="h-3.5 w-3.5" /> {t("receipt")}
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          </CardContent></Card>
        </section>
      )}
    </div>
  );
}

// Small alias so lucide's CheckCheck can be reused above (avoid extra import).
function CheckCheck(props) {
  return <CheckCircle2 {...props} />;
}
